// qs-useModeManager.ts
import { computed, onUnmounted, readonly, ref } from 'vue'
import { useEventListener } from '@vueuse/core'
import { cursorManager } from '@/utils/qs-cursorManager'
import type {
  ModeConfig,
  ModeEvent,
  ModeState,
  ModeType,
  ModeValidation
} from '../types/qs-type-modes'
import {
  isDestructiveMode,
  MODE_REGISTRY
} from '../types/qs-type-modes'

const globalModeState = ref<ModeState>({
  activeMode: 'drawing',
  previousMode: null,
  isTransitioning: false,
  modeStartTime: null,
  activeLayerId: null
})

setTimeout(() => {
  if (globalModeState.value.activeMode !== 'drawing') {
    globalModeState.value.activeMode = 'drawing'
    globalModeState.value.previousMode = null
    cursorManager.resetToDrawing()
  }
}, 100)

const modeEventListeners = new Set<(event: ModeEvent) => void>()

let eventListenerStops: Array<() => void> = []
let isInitialized = false

export function useModeManager() {
  const activeMode = computed(() => globalModeState.value.activeMode)
  const previousMode = computed(() => globalModeState.value.previousMode)
  const isTransitioning = computed(() => globalModeState.value.isTransitioning)
  const modeStartTime = computed(() => globalModeState.value.modeStartTime)

  const isDrawingMode = computed(() => activeMode.value === 'drawing')
  const isDeleteMode = computed(() => activeMode.value === 'delete')
  const isPanMode = computed(() => activeMode.value === 'pan')
  const isPositionMode = computed(() => activeMode.value === 'position')
  const isTextMode = computed(() => activeMode.value === 'text')

  const currentModeConfig = computed(() =>
    activeMode.value ? getModeConfig(activeMode.value) : null
  )

  const getModeConfig = (mode: ModeType): ModeConfig | null => {
    return mode ? MODE_REGISTRY[mode] || null : null
  }

  const getAvailableModes = (): ModeConfig[] => {
    return Object.values(MODE_REGISTRY)
  }

  const getModesByCategory = (category: string): ModeConfig[] => {
    return Object.values(MODE_REGISTRY).filter(mode => mode.category === category)
  }

  const emitModeEvent = (event: ModeEvent) => {
    modeEventListeners.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        console.error('Mode event listener error:', error)
      }
    })
  }

  const onModeEvent = (listener: (event: ModeEvent) => void) => {
    modeEventListeners.add(listener)
    return () => modeEventListeners.delete(listener)
  }

  const validateMode = (mode: ModeType): ModeValidation => {
    if (!mode) {
      return { isValid: true, canActivate: true }
    }

    const config = getModeConfig(mode)
    if (!config) {
      return {
        isValid: false,
        canActivate: false,
        reason: `Unknown mode: ${mode}`
      }
    }

    if (activeMode.value === mode) {
      return {
        isValid: true,
        canActivate: false,
        reason: 'Mode is already active'
      }
    }

    if (isDestructiveMode(mode) && isDestructiveMode(activeMode.value)) {
      return {
        isValid: true,
        canActivate: false,
        reason: 'Cannot activate destructive mode while another destructive mode is active'
      }
    }

    return { isValid: true, canActivate: true }
  }

  const activateMode = async (mode: ModeType): Promise<void> => {
    if (!mode) return

    if (mode === 'drawing') {
      try {
        const { useToolStore } = await import('@/stores/qs-store-tools')
        const toolStore = useToolStore()
        const brushSize = toolStore.currentTool?.size || 8
        cursorManager.setCursor(mode, brushSize)
      } catch (error) {
        const { DEFAULT_BRUSH_SIZE } = await import('@/config/qs-brushDefaults')
        cursorManager.setCursor(mode, DEFAULT_BRUSH_SIZE)
      }
    } else {
      cursorManager.setCursor(mode)
    }
  }

  const deactivateMode = async (mode: ModeType): Promise<void> => {
    if (!mode) return

    cursorManager.resetToDrawing()
  }

  const setMode = async (mode: ModeType, force = false): Promise<boolean> => {
    if (!force) {
      const validation = validateMode(mode)
      if (!validation.canActivate) {
        console.warn(`Cannot activate mode ${mode}:`, validation.reason)
        emitModeEvent({
          type: 'mode-failed',
          mode,
          previousMode: activeMode.value,
          timestamp: Date.now(),
          reason: validation.reason || 'Unknown error'
        })
        return false
      }
    }

    globalModeState.value.isTransitioning = true
    const previousModeValue = activeMode.value
    const startTime = Date.now()

    try {
      if (previousModeValue) {
        await deactivateMode(previousModeValue)
      }

      if (mode) {
        await activateMode(mode)
      }

      globalModeState.value.previousMode = previousModeValue
      globalModeState.value.activeMode = mode
      globalModeState.value.modeStartTime = startTime

      if (mode !== 'position') {
        globalModeState.value.activeLayerId = null
      }

      if (mode) {
        emitModeEvent({
          type: 'mode-activated',
          mode,
          previousMode: previousModeValue,
          timestamp: startTime
        })
      }

      if (previousModeValue) {
        emitModeEvent({
          type: 'mode-deactivated',
          mode: previousModeValue,
          previousMode: null,
          timestamp: startTime
        })
      }

      if (previousModeValue && mode) {
        emitModeEvent({
          type: 'mode-switched',
          mode,
          previousMode: previousModeValue,
          timestamp: startTime
        })
      }

      return true
    } catch (error) {
      console.error('Mode activation failed:', error)
      emitModeEvent({
        type: 'mode-failed',
        mode,
        previousMode: previousModeValue,
        timestamp: startTime,
        reason: error instanceof Error ? error.message : 'Unknown error'
      })
      return false
    } finally {
      globalModeState.value.isTransitioning = false
    }
  }

  const activateDeleteMode = () => setMode('delete')
  const activatePanMode = () => setMode('pan')
  const activatePositionMode = (layerId?: string) => {
    if (layerId) {
      globalModeState.value.activeLayerId = layerId
    }
    return setMode('position')
  }
  const activateTextMode = () => setMode('text')

  const deactivateAllModes = () => setMode(null)
  const deactivateCurrentMode = () => setMode(null)

  const switchToDrawingMode = () => setMode('drawing')
  const switchToPreviousMode = () => {
    const prev = previousMode.value
    if (prev) {
      setMode(prev)
    }
  }

  if (!isInitialized) {
    const stop1 = useEventListener(window, 'tool-changed', () => {
      if (activeMode.value && activeMode.value !== 'drawing') {
        switchToDrawingMode()
      }
    })
    eventListenerStops.push(stop1)

    const stop2 = useEventListener(window, 'layer-changed', () => {
      if (activeMode.value && activeMode.value !== 'drawing' && activeMode.value !== 'position') {
        switchToDrawingMode()
      }
    })
    eventListenerStops.push(stop2)

    const stop3 = useEventListener(window, 'file-changed', () => {
      if (activeMode.value && activeMode.value !== 'drawing') {
        switchToDrawingMode()
      }
    })
    eventListenerStops.push(stop3)

    const stop4 = useEventListener(window, 'history-changed', () => {
      if (activeMode.value && activeMode.value !== 'drawing') {
        switchToDrawingMode()
      }
    })
    eventListenerStops.push(stop4)

    const stop5 = useEventListener(window, 'keydown', (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return

    if (cursorManager.isAnyInputActive()) {
      return
    }

    const key = e.key.toLowerCase()
    switch (key) {
      case 'd':
        if (activeMode.value === 'delete') {
          switchToDrawingMode()
        } else {
          activateDeleteMode()
        }
        break

      case 'escape':
        switchToDrawingMode()
        break
    }
    })
    eventListenerStops.push(stop5)
    
    isInitialized = true
  }

  onUnmounted(() => {
    modeEventListeners.clear()
    eventListenerStops.forEach(stop => stop())
    eventListenerStops = []
    isInitialized = false
  })

  return {
    activeMode: readonly(activeMode),
    previousMode: readonly(previousMode),
    isTransitioning: readonly(isTransitioning),
    modeStartTime: readonly(modeStartTime),
    currentModeConfig: readonly(currentModeConfig),
    globalModeState: readonly(globalModeState),
    isDrawingMode,
    isDeleteMode,
    isPanMode,
    isPositionMode,
    isTextMode,
    setMode,
    activateDeleteMode,
    activatePanMode,
    activatePositionMode,
    activateTextMode,
    deactivateAllModes,
    deactivateCurrentMode,
    switchToDrawingMode,
    switchToPreviousMode,
    validateMode,
    getModeConfig,
    getAvailableModes,
    getModesByCategory,
    onModeEvent,
    activateMode,
    deactivateMode
  }
}